import React, { useMemo } from 'react';
import '../assets/styles/shipping_manifest_list.scss';
import { getIncotermsLabel, getModeOfTransportLabel } from '../utils/shippingManifestView';

export default function ShippingManifestDetailsModal({
  open,
  manifest,
  onClose,
  shippingTermsOptions,
  modeOfTransportOptions,
  showAcceptButton = false,
  onAccept,
  acceptDisabled = false,
  acceptLabel = 'Accept Manifest',
  /** Optional buttons (e.g. View GRN + Accept GRN) rendered beside Accept Manifest */
  extraHeaderActions = null,
}) {
  const modalTitleId = useMemo(() => `sml-view-modal-title-${manifest?.id || 'x'}`, [manifest?.id]);
  const modalDescriptionId = useMemo(() => `sml-view-modal-desc-${manifest?.id || 'x'}`, [manifest?.id]);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const renderDocumentRow = (docKey, label, isRequired = false) => {
    const uploaded = manifest?.documents?.[docKey];
    const fileInfo = manifest?.documentFiles?.[docKey];
    const hasFile = Boolean(uploaded && fileInfo?.name);

    return (
      <div className="sml-doc-row" key={`${docKey}-${manifest?.id || 'x'}`}>
        <div className="sml-doc-info">
          <p className="sml-doc-label">
            {label}
            {isRequired && <span className="sml-doc-required">*</span>}
          </p>
          {hasFile ? (
            <>
              <p className="sml-doc-file">{fileInfo.name}</p>
              {fileInfo?.type && <p className="sml-doc-meta">{fileInfo.type}</p>}
            </>
          ) : (
            <p className="sml-doc-file sml-doc-file-muted">No file uploaded</p>
          )}
        </div>
        {hasFile ? (
          <span className="sml-chip sml-chip-success">Uploaded</span>
        ) : (
          <span className="sml-chip sml-chip-muted">Pending</span>
        )}
      </div>
    );
  };

  if (!open || !manifest) return null;

  return (
    // `shipping_manifest_list.scss` scopes modal styles under `.shipping-manifest-list`.
    // Wrap the modal so the exact same design is applied everywhere it is reused.
    <div className="shipping-manifest-list">
      <div className="sml-view-modal-overlay" onClick={onClose}>
        <div
          className="sml-view-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          aria-describedby={modalDescriptionId}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="sml-view-modal-close" onClick={onClose} aria-label="Close manifest details">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

        <div className="sml-view-modal-header">
          <div>
            <h2 id={modalTitleId} className="sml-view-modal-title">
              Shipping Manifest - {manifest.manifestNumber}
            </h2>
            <p id={modalDescriptionId} className="sml-view-modal-subtitle">
              Detailed shipment overview
            </p>
          </div>
          {extraHeaderActions || showAcceptButton ? (
            <div className="d-flex flex-wrap align-items-start justify-content-end gap-2">
              {extraHeaderActions}
              {showAcceptButton ? (
                <button
                  type="button"
                  className="sml-doc-view-btn"
                  onClick={onAccept}
                  disabled={acceptDisabled || typeof onAccept !== 'function'}
                >
                  {acceptLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="sml-view-summary-grid">
          <div className="sml-view-summary-card">
            <p className="sml-view-summary-label">Manifest Number</p>
            <p className="sml-view-summary-value">{manifest.manifestNumber}</p>
          </div>
          <div className="sml-view-summary-card">
            <p className="sml-view-summary-label">Manifest Date</p>
            <p className="sml-view-summary-value">
              {manifest.manifestDate ? formatDate(manifest.manifestDate) : '—'}
            </p>
          </div>
          <div className="sml-view-summary-card">
            <p className="sml-view-summary-label">Shipping Method</p>
            <p className="sml-view-summary-value">{manifest.shippingMethod}</p>
          </div>
          <div className="sml-view-summary-card">
            <p className="sml-view-summary-label">LR / Reference</p>
            <p className="sml-view-summary-value">{manifest.lrNumber || '—'}</p>
          </div>
          <div className="sml-view-summary-card">
            <p className="sml-view-summary-label">Status</p>
            <p className="sml-view-summary-value">{manifest.status}</p>
          </div>
        </div>

        <div className="sml-view-two-col">
          <div className="sml-view-card">
            <p className="sml-view-card-label">Exporter</p>
            <p className="sml-view-card-value">{manifest.exporter}</p>
            <p className="sml-view-card-subtext">{manifest.exporterLocation}</p>
          </div>
          <div className="sml-view-card">
            <p className="sml-view-card-label">Consignee</p>
            <p className="sml-view-card-value">{manifest.importer}</p>
            <p className="sml-view-card-subtext">{manifest.importerLocation}</p>
          </div>
        </div>

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Shipment Details</h4>
          <div className="sml-view-details-grid sml-view-details-grid-six">
            <div>
              <p className="sml-view-detail-label">Preferred Delivery Port</p>
              <p className="sml-view-detail-value">{manifest.deliveryPort || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Delivery Port Reference</p>
              <p className="sml-view-detail-value">{manifest.deliveryPortCode || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Invoice / Bill Number</p>
              <p className="sml-view-detail-value">{manifest.invoiceNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">E-way Bill Number</p>
              <p className="sml-view-detail-value">{manifest.ewayBillNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Mode of Transport</p>
              <p className="sml-view-detail-value">
                {getModeOfTransportLabel(manifest.mode || manifest.shippingMethod, modeOfTransportOptions) || '—'}
              </p>
            </div>
            <div>
              <p className="sml-view-detail-label">Incoterms</p>
              <p className="sml-view-detail-value">
                {getIncotermsLabel(manifest.incoterms, shippingTermsOptions) || '—'}
              </p>
            </div>
            <div>
              <p className="sml-view-detail-label">Estimated Delivery Date</p>
              <p className="sml-view-detail-value">
                {manifest.expectedArrival ? formatDate(manifest.expectedArrival) : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Vehicle & Driver Details</h4>
          <div className="sml-view-details-grid sml-view-details-grid-six">
            <div>
              <p className="sml-view-detail-label">Vehicle Type</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.vehicleType || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Vehicle Number</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.vehicleNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Vehicle Capacity</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.vehicleCapacity || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">LR Number (Auto-generated)</p>
              <p className="sml-view-detail-value">{manifest.lrNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Driver Name</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.driverName || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Driver Contact Number</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.driverContactNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Driver License</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.driverLicenseNo || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">GPS Tracking ID</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.gpsTrackingId || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">GPS Provider</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.gpsProvider || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Insurance Policy No.</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.insurancePolicyNo || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Insurance Provider</p>
              <p className="sml-view-detail-value">{manifest.vehicleDetails?.insuranceProvider || '—'}</p>
            </div>
          </div>
        </div>

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Goods Summary</h4>
          <div className="sml-view-summary-grid">
            <div className="sml-view-summary-card">
              <p className="sml-view-summary-label">Packages</p>
              <p className="sml-view-summary-value">{manifest.totals?.packages ?? 0}</p>
            </div>
            <div className="sml-view-summary-card">
              <p className="sml-view-summary-label">Gross Weight</p>
              <p className="sml-view-summary-value">{manifest.totals?.grossWeight || '—'}</p>
            </div>
            <div className="sml-view-summary-card">
              <p className="sml-view-summary-label">Net Weight</p>
              <p className="sml-view-summary-value">{manifest.totals?.netWeight || '—'}</p>
            </div>
          </div>
        </div>

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Goods Handling Requirements</h4>
          <div className="sml-view-details-grid sml-view-details-grid-six">
            <div>
              <p className="sml-view-detail-label">Hazardous Material</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.hazardousMaterial ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Hazard Class</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.hazardClass || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">UN Number</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.unNumber || '—'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Temperature Controlled</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.temperatureControlled ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="sml-view-detail-label">Min Temperature</p>
              <p className="sml-view-detail-value">
                {manifest.goodsHandling?.minTemperature != null && manifest.goodsHandling?.minTemperature !== ''
                  ? String(manifest.goodsHandling.minTemperature)
                  : '—'}
              </p>
            </div>
            <div>
              <p className="sml-view-detail-label">Max Temperature</p>
              <p className="sml-view-detail-value">
                {manifest.goodsHandling?.maxTemperature != null && manifest.goodsHandling?.maxTemperature !== ''
                  ? String(manifest.goodsHandling.maxTemperature)
                  : '—'}
              </p>
            </div>
            <div>
              <p className="sml-view-detail-label">Fragile</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.fragile ? 'Yes' : 'No'}</p>
            </div>
            <div style={{ gridColumn: 'span 6' }}>
              <p className="sml-view-detail-label">Special Handling</p>
              <p className="sml-view-detail-value">{manifest.goodsHandling?.specialHandling || '—'}</p>
            </div>
          </div>
        </div>

        {manifest.additionalNotes ? (
          <div className="sml-view-section">
            <h4 className="sml-view-section-title">Remarks / Notes</h4>
            <div className="sml-view-card">
              <p className="sml-view-card-value">{manifest.additionalNotes}</p>
            </div>
          </div>
        ) : null}

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Item &amp; Packaging Details</h4>
          <div className="sml-view-table-wrapper">
            <table className="sml-view-table sml-view-table-compact">
              <thead>
                <tr>
                  <th>HSN / Product Code</th>
                  <th>Product Description</th>
                  <th>Qty Shipped</th>
                  <th>Qty Total</th>
                  <th>Shipping Qty</th>
                  <th>UOM</th>
                  <th>Package Type</th>
                  <th>No. Packages</th>
                  <th>Weight / Pkg</th>
                  <th className="sml-text-right">Total Weight</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(manifest.packageItems) && manifest.packageItems.length > 0 ? (
                  <>
                    {manifest.packageItems.map((item, index) => (
                      <tr key={`pkg-${item.hsnProductCode}-${index}`}>
                        <td>{item.hsnProductCode}</td>
                        <td>{item.productDescription}</td>
                        <td>{item.qtyShipped}</td>
                        <td>{item.qtyTotal}</td>
                        <td>{item.shippingQty}</td>
                        <td>{item.uom}</td>
                        <td>{item.packageType}</td>
                        <td>{item.noPackages}</td>
                        <td>{item.weightPerPkg}</td>
                        <td className="sml-text-right">{item.totalWeight}</td>
                      </tr>
                    ))}
                    <tr className="sml-view-table-total-row">
                      <td colSpan="1"></td>
                      <td colSpan="1" className="sml-view-table-total-label">
                        Totals
                      </td>
                      <td colSpan="5"></td>
                      <td>{manifest.totals?.packages || manifest.packages}</td>
                      <td />
                      <td className="sml-text-right">{manifest.totals?.grossWeight || manifest.weight}</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan="10" className="sml-view-table-empty">
                      No item / packaging details recorded for this manifest.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sml-view-section">
          <h4 className="sml-view-section-title">Supporting Documents</h4>
          <div className="sml-doc-section">
            <div className="sml-doc-column sml-doc-column--required">
              <p className="sml-doc-column-title">Required Documents</p>
              <div className="sml-doc-required-grid">
                {renderDocumentRow('invoice', 'Invoice Copy', true)}
                {renderDocumentRow('ewayBill', 'E-way Bill', true)}
              </div>
              <p className="sml-doc-column-title sml-doc-optional-title">Optional Documents</p>
              <div className="sml-doc-required-grid sml-doc-optional-grid">
                <div className="sml-doc-row-wrapper">{renderDocumentRow('packingList', 'Packing List')}</div>
                <div className="sml-doc-row-wrapper">{renderDocumentRow('qualityCert', 'Quality Certificate')}</div>
                <div className="sml-doc-row-wrapper">{renderDocumentRow('certOfOrigin', 'Certificate of Origin')}</div>
                <div className="sml-doc-row-wrapper">{renderDocumentRow('otherDocs', 'Other Documents')}</div>
              </div>
            </div>
          </div>
        </div>

        {manifest.acceptanceDetails ? (
          <div className="sml-view-section">
            <h4 className="sml-view-section-title">Acceptance Details</h4>
            <div className="sml-view-details-grid sml-view-details-grid-six">
              <div>
                <p className="sml-view-detail-label">Date Received</p>
                <p className="sml-view-detail-value">
                  {manifest.acceptanceDetails.dateReceived ? formatDate(manifest.acceptanceDetails.dateReceived) : '—'}
                </p>
              </div>
              <div>
                <p className="sml-view-detail-label">Accepted Date</p>
                <p className="sml-view-detail-value">
                  {manifest.acceptanceDetails.acceptedDate ? formatDate(manifest.acceptanceDetails.acceptedDate) : '—'}
                </p>
              </div>
              <div>
                <p className="sml-view-detail-label">Received By</p>
                <p className="sml-view-detail-value">{manifest.acceptanceDetails.receivedBy || '—'}</p>
              </div>
              <div>
                <p className="sml-view-detail-label">Packages Received</p>
                <p className="sml-view-detail-value">
                  {manifest.acceptanceDetails.packagesReceived || manifest.totals?.packages || manifest.packages || '—'}
                </p>
              </div>
              <div style={{ gridColumn: 'span 6' }}>
                <p className="sml-view-detail-label">Acceptance Notes</p>
                <p className="sml-view-detail-value">{manifest.acceptanceDetails.additionalNotes || '—'}</p>
              </div>
              {manifest.acceptanceDetails.podFile?.path ? (
                <div style={{ gridColumn: 'span 6' }}>
                  <p className="sml-view-detail-label">Proof of Delivery (POD)</p>
                  <p className="sml-view-detail-value">{manifest.acceptanceDetails.podFile.name || 'POD'}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        </div>
      </div>
    </div>
  );
}

